import { reactive } from 'vue'
import debounce from 'lodash/debounce'

import { getItem, saveItem } from '@/plugins/storage'

const STORAGE_KEY = 'visibleMarkups'

/**
 * The user's explicit show/hide choice for each markup, keyed by uuid.
 *
 * This is deliberately separate from markup-service's `_visibleMarkups`, which
 * holds *effective* visibility and gets overwritten every time a content filter
 * runs (see `setFilteredVisibility`). Anything that needs to know what the user
 * actually chose -- filters especially, or they end up feeding themselves --
 * reads this map instead.
 *
 * It lives in its own module so `filter-markup-service` can read it without
 * importing `markup-service`, which already imports the filter service.
 */
const storedVisibility: { [uuid: string]: boolean } = reactive({})

// Null until the first load so callers can tell "nothing stored yet" apart from
// "stored as hidden"; writes before that point are dropped, as they always were.
let isLoaded = false

// debounced to help with performance when saving a bunch of changes at once.
const save: () => void = debounce(() => {
  saveItem(STORAGE_KEY, { ...storedVisibility })
}, 200)

/**
 * Whether the stored visibility state has been read out of browser storage yet.
 */
export const isStoredVisibilityLoaded = () => isLoaded

/**
 * Loads the stored visibility state from browser storage on first call, and
 * returns a plain copy of it. Later calls return a copy of the current state
 * without re-reading storage.
 *
 * @returns Snapshot of the stored visibility state, keyed by markup uuid
 */
export const loadStoredVisibility = () => {
  if (!isLoaded) {
    Object.assign(storedVisibility, getItem(STORAGE_KEY) ?? {})
    isLoaded = true
  }

  return { ...storedVisibility }
}

/**
 * Looks up the user's stored visibility choice for a markup.
 *
 * @param uuid - UUID of markup to look up
 * @returns True if visible, false if hidden, undefined if nothing is stored
 */
export const getStoredVisibility = (uuid: string) => {
  return isLoaded ? storedVisibility[uuid] : undefined
}

/**
 * Records the user's visibility choice for a markup and persists it.
 *
 * @param uuid - UUID of markup to update
 * @param visible - Whether the markup should be visible
 */
export const setStoredVisibility = (uuid: string, visible: boolean) => {
  if (!isLoaded) {
    return
  }

  storedVisibility[uuid] = visible
  save()
}

/**
 * Drops the stored visibility choice for a markup, for example on delete.
 *
 * @param uuid - UUID of markup to clear
 */
export const clearStoredVisibility = (uuid: string) => {
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete storedVisibility[uuid]
  save()
}

/**
 * Persists the current stored visibility state.
 */
export const saveStoredVisibility = () => save()

/**
 * Whether the user has explicitly hidden this markup. A markup with nothing
 * stored counts as visible, matching how markups are initialized on load.
 *
 * @param uuid - UUID of markup to check
 * @returns True only if the markup is stored as hidden
 */
export const isHiddenByUser = (uuid: string) =>
  getStoredVisibility(uuid) === false
